import React from "react";
import { MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useRetailer } from "../../hooks/useRetailer";
import { getSearchParams, shouldFallbackToSku } from "./CheckStockButton.utils";

interface CheckStockButtonProps {
  productSku: string;
  productUpc?: string; // UPC/ItemId for Home Depot
  className?: string;
  label?: string;
  onSearch?: (params: {
    searchCode: string;
    searchType: "UPC" | "SKU";
    fallbackCode?: string;
    fallbackType?: "SKU";
  }) => void | Promise<void>;
}

const CheckStockButton: React.FC<CheckStockButtonProps> = ({
  productSku,
  productUpc,
  className = "",
  label = "Check stock",
  onSearch,
}) => {
  const currentRetailer = useRetailer();
  const navigate = useNavigate();

  const handleNearbySearch = async () => {
    const { searchCode, searchType } = getSearchParams({
      retailer: currentRetailer,
      productSku,
      productUpc,
    });
    const shouldUseSkuFallback = shouldFallbackToSku({
      retailer: currentRetailer,
      productUpc,
      productSku,
    });
    if (onSearch) {
      await onSearch({
        searchCode,
        searchType,
        fallbackCode: shouldUseSkuFallback && productSku ? productSku : undefined,
        fallbackType: shouldUseSkuFallback && productSku ? "SKU" : undefined,
      });
      return;
    }

    const params = new URLSearchParams();
    params.set(searchType === "UPC" ? "upc" : "sku", searchCode);
    params.set("auto", "true");
    if (shouldUseSkuFallback && productSku) {
      params.set("fallbackSku", productSku);
    }
    navigate(`/${currentRetailer}/product-search?${params.toString()}`);
  };

  return (
    <>
      <button
        onClick={handleNearbySearch}
        className={`group relative inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 bg-emerald-50 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 hover:shadow-sm active:scale-95 ${className}`}
      >
        <MapPin className="w-3.5 h-3.5 group-hover:scale-110 transition-transform duration-200" />
        <span>{label}</span>

        {/* Subtle gradient overlay on hover */}
        <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-emerald-500/0 to-emerald-600/0 group-hover:from-emerald-500/5 group-hover:to-emerald-600/5 dark:group-hover:from-emerald-500/10 dark:group-hover:to-emerald-600/10 transition-all duration-200" />
      </button>

    </>
  );
};

export default CheckStockButton;
